import { QueryClient, QueryFunction } from "@tanstack/react-query";

type EntitlementErrorHandler = (data: { message: string; plan: string }) => void;

let entitlementErrorHandler: EntitlementErrorHandler | null = null;

export function setEntitlementErrorHandler(handler: EntitlementErrorHandler) {
  entitlementErrorHandler = handler;
}

function tryHandle403(status: number, bodyText: string) {
  if (status !== 403 || !entitlementErrorHandler) return;
  try {
    const data = JSON.parse(bodyText);
    if (data.plan !== undefined || (data.message && (data.message.includes("upgrade") || data.message.includes("limit")))) {
      entitlementErrorHandler({ message: data.message, plan: data.plan || "free" });
    }
  } catch {}
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    tryHandle403(res.status, text);
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  opts?: { timeoutMs?: number },
): Promise<Response> {
  const timeoutMs = opts?.timeoutMs ?? 45000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      signal: controller.signal,
    });

    await throwIfResNotOk(res);
    return res;
  } catch (err: any) {
    if (err.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);

    try {
      const res = await fetch(queryKey.join("/") as string, {
        credentials: "include",
        signal: controller.signal,
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (err: any) {
      if (err.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  };

export const STALE_TIMES = {
  REALTIME: 30 * 1000,
  FRESH: 2 * 60 * 1000,
  SEMI_FRESH: 5 * 60 * 1000,
  STATIC: 30 * 60 * 1000,
} as const;

export const queryKeys = {
  auth: {
    me: ["/api/auth/me"] as const,
  },
  student: {
    bundle: (childId: string) => ["/api/student", childId, "bundle"] as const,
  },
  children: {
    all: ["/api/children"] as const,
    detail: (childId: string) => ["/api/children", childId] as const,
    dashboardBundle: (childId: string) => ["/api/children", childId, "dashboard-bundle"] as const,
    mastery: (childId: string) => ["/api/children", childId, "mastery"] as const,
    sessions: (childId: string) => ["/api/children", childId, "sessions"] as const,
    performance: (childId: string) => ["/api/children", childId, "performance"] as const,
    misconceptions: (childId: string) => ["/api/children", childId, "misconceptions"] as const,
    subjects: (childId: string) => ["/api/children", childId, "subjects"] as const,
    weeklySummary: (childId: string) => ["/api/children", childId, "weekly-summary"] as const,
    mockTests: (childId: string) => ["/api/children", childId, "mock-tests"] as const,
    mockTrend: (childId: string) => ["/api/children", childId, "mock-trend"] as const,
    examReadiness: (childId: string) => ["/api/children", childId, "exam-readiness"] as const,
    studySchedule: (childId: string) => ["/api/children", childId, "study-schedule"] as const,
    examDates: (childId: string) => ["/api/children", childId, "exam-dates"] as const,
    revisionTimetable: (childId: string) => ["/api/children", childId, "revision-timetable"] as const,
    inventory: (childId: string) => ["/api/children", childId, "inventory"] as const,
    progression: (childId: string) => ["/api/children", childId, "progression"] as const,
    avatarLoadout: (childId: string) => ["/api/children", childId, "avatar-loadout"] as const,
    milestones: (childId: string) => ["/api/children", childId, "milestones"] as const,
    access: (childId: string) => ["/api/children", childId, "access"] as const,
    gamification: (childId: string) => ["/api/children", childId, "gamification"] as const,
    novaInsights: (childId: string) => ["/api/children", childId, "nova-insights"] as const,
    safetySummary: (childId: string) => ["/api/children", childId, "safety-summary"] as const,
    sessionEvidence: (childId: string) => ["/api/children", childId, "session-evidence"] as const,
    interventionPlan: (childId: string) => ["/api/children", childId, "intervention-plan"] as const,
    reviewQueue: (childId: string) => ["/api/children", childId, "review-queue"] as const,
    comeback: (childId: string) => ["/api/children", childId, "comeback"] as const,
    badges: (childId: string) => ["/api/children", childId, "badges"] as const,
  },
  mockTests: {
    results: (testId: string) => ["/api/mock-tests", testId, "results"] as const,
  },
  analytics: {
    summary: ["/api/analytics/summary"] as const,
  },
  entitlement: ["/api/entitlement"] as const,
  examFamilies: {
    all: ["/api/exam-families"] as const,
    pathways: (familyId: string) => ["/api/exam-families", familyId, "pathways"] as const,
  },
  sync: {
    devices: ["/api/sync/devices"] as const,
  },
  admin: {
    stats: ["/api/admin/stats"] as const,
    examFamilies: {
      all: ["/api/admin/exam-families"] as const,
      pathways: (familyId: string) => ["/api/admin/exam-families", familyId, "pathways"] as const,
      subjects: (familyId: string) => ["/api/admin/exam-families", familyId, "subjects"] as const,
    },
    subjects: {
      topics: (subjectId: string) => ["/api/admin/subjects", subjectId, "topics"] as const,
    },
    topics: {
      skills: (topicId: string) => ["/api/admin/topics", topicId, "skills"] as const,
    },
    concepts: ["/api/admin/concepts"] as const,
    contentItems: ["/api/admin/content-items"] as const,
    entitlements: ["/api/admin/entitlements"] as const,
    safetyEvents: ["/api/admin/safety-events"] as const,
    documents: (...filters: any[]) => ["/api/admin/documents", ...filters] as const,
    generationRuns: ["/api/admin/generation-runs"] as const,
    coverageReport: ["/api/admin/coverage-report"] as const,
    boardProfiles: {
      all: ["/api/admin/board-profiles"] as const,
      detail: (profileId: string) => [`/api/admin/board-profiles/${profileId}`] as const,
    },
    pastPaperGaps: ["/api/admin/past-paper-gaps"] as const,
    content: {
      examBoards: ["/api/admin/content/exam-boards"] as const,
      qualifications: ["/api/admin/content/qualifications"] as const,
      subjectsList: ["/api/admin/subjects-list"] as const,
      coverage: ["/api/admin/content/coverage"] as const,
      resources: (...filters: any[]) => ["/api/admin/content/resources", ...filters] as const,
      specifications: (qualId?: string) => qualId ? ["/api/admin/content/specifications", qualId] as const : ["/api/admin/content/specifications"] as const,
      paperComponents: (specId?: string) => specId ? ["/api/admin/content/paper-components", specId] as const : ["/api/admin/content/paper-components"] as const,
      assessmentObjectives: (specId?: string) => specId ? ["/api/admin/content/assessment-objectives", specId] as const : ["/api/admin/content/assessment-objectives"] as const,
      curriculumTree: (specId?: string) => specId ? ["/api/admin/content/curriculum-tree", specId] as const : ["/api/admin/content/curriculum-tree"] as const,
      questionPatterns: (specId?: string) => specId ? ["/api/admin/content/question-patterns", specId] as const : ["/api/admin/content/question-patterns"] as const,
      traceability: (type?: string, id?: string) =>
        type && id ? ["/api/admin/content/traceability", type, id] as const :
        type ? ["/api/admin/content/traceability", type] as const :
        ["/api/admin/content/traceability"] as const,
      reviewQueue: ["/api/admin/content/review-queue"] as const,
    },
  },
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIMES.SEMI_FRESH,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
